/**
 * The terms of a campaign request, as a brand submits them (specification §4).
 *
 * The schema checks each field on its own: types, formats and lengths. Rules that relate fields
 * to each other or to the platform's state (dates, the budget's multiple of 5, accepted tokens)
 * are in `rules.ts`. The admin team reviews the terms and may adjust them before accepting. The
 * accepted terms become the campaign manifest.
 */

import { z } from "zod";

/** The platforms a campaign can run on (§4.1). */
export const PLATFORMS = ["X", "YOUTUBE"] as const;
export type Platform = (typeof PLATFORMS)[number];

/** YouTube campaign formats (§4.1). An X campaign has no format. */
export const YOUTUBE_FORMATS = ["DEDICATED", "INTEGRATION"] as const;
export type YoutubeFormat = (typeof YOUTUBE_FORMATS)[number];

/** Most items in each list of a brief, so that every requirement can be checked one by one. */
export const MAX_BRIEF_LIST_ITEMS = 10;

/** Most images in a brief's brand kit (D24). */
export const MAX_BRAND_ASSETS = 5;

/** Largest image of a brand kit, in bytes. */
export const MAX_BRAND_ASSET_BYTES = 5 * 1024 * 1024;

/** The image types a brand kit takes, each with the extension of its published file. */
export const BRAND_ASSET_TYPES = {
  "image/png": "png",
  "image/jpeg": "jpg",
  "image/webp": "webp",
} as const;
export type BrandAssetType = keyof typeof BRAND_ASSET_TYPES;

/**
 * An image of the brand kit. Its name, which visual requirements may use, such as "Logo". Its
 * SHA-256, which names the uploaded file and binds it to the manifest, and its type.
 */
export const brandAssetSchema = z.object({
  label: z.string().trim().min(1).max(40),
  sha256: z.string().regex(/^[0-9a-f]{64}$/, "must be a SHA-256 in lowercase hex"),
  mediaType: z.enum(Object.keys(BRAND_ASSET_TYPES) as [BrandAssetType, ...BrandAssetType[]]),
});
export type BrandAsset = z.output<typeof brandAssetSchema>;

/** Where an uploaded brand image is kept in the media bucket. */
export function brandAssetKey(sha256: string): string {
  return `brand-assets/${sha256}`;
}

/** The published file name of a brand image, such as `0x5e5e….png`. */
export function brandAssetFile(asset: Pick<BrandAsset, "sha256" | "mediaType">): string {
  return `0x${asset.sha256}.${BRAND_ASSET_TYPES[asset.mediaType]}`;
}

/** A list of brief items: each trimmed, of the given length, at most ten, none repeated. */
function itemList(item: z.ZodType<string, string>) {
  return z
    .array(item)
    .max(MAX_BRIEF_LIST_ITEMS)
    .refine((items) => new Set(items).size === items.length, "must not repeat an item")
    .default([]);
}

/**
 * What creators' content must and must not contain. Each list item is checked separately during
 * verification (specification §8.1), so it should state one requirement.
 */
export const briefSchema = z.object({
  /** Short name of the campaign, shown to creators. */
  title: z.string().trim().min(3).max(120),

  /** What the campaign promotes and the key messages, in the brand's words. */
  description: z.string().trim().min(20).max(5_000),

  /** Links the content must include, e.g. the product page. */
  requiredLinks: itemList(z.url({ protocol: /^https?$/ }).max(500)),

  /** Hashtags the content must include, with the leading #. */
  requiredHashtags: itemList(
    z.string().regex(/^#[\p{L}\p{N}_]{1,100}$/u, "must be a hashtag such as #morcast"),
  ),

  /** Accounts the content must mention, with the leading @. */
  requiredMentions: itemList(
    z.string().regex(/^@[A-Za-z0-9_.-]{1,100}$/, "must be an account such as @morcast"),
  ),

  /** Exact phrases the content must contain. */
  requiredText: itemList(z.string().trim().min(1).max(200)),

  /** The paid-promotion disclosure the content must show, e.g. "#ad". */
  disclosure: z.string().trim().min(2).max(100),

  /** Statements about the content that the semantic check evaluates one by one. */
  semanticRequirements: itemList(z.string().trim().min(10).max(300)),

  /** Statements about what the content must show, for the visual checks. */
  visualRequirements: itemList(z.string().trim().min(10).max(300)),

  /** What the content must not contain. */
  prohibitedContent: itemList(z.string().trim().min(3).max(300)),

  /** Changes a creator may make after submitting. Anything else fails the edit check. */
  allowedEdits: z.string().trim().max(500).default(""),

  /**
   * The brand kit (D24): images creators may use, such as the logo, which the visual checks also
   * show the model next to the content. Each name and each file appears once.
   */
  assets: z
    .array(brandAssetSchema)
    .max(MAX_BRAND_ASSETS)
    .refine(
      (assets) => new Set(assets.map((asset) => asset.label.toLowerCase())).size === assets.length,
      "must not repeat a name",
    )
    .refine(
      (assets) => new Set(assets.map((asset) => asset.sha256)).size === assets.length,
      "must not repeat an image",
    )
    .default([]),
});

export type Brief = z.output<typeof briefSchema>;

/** A positive integer as a decimal string, such as a budget in base units ("100000000"). */
const positiveInteger = z
  .string()
  .regex(/^[1-9]\d{0,77}$/, "must be a positive whole number written in digits")
  .transform((value) => BigInt(value));

/** An EVM address in any letter case, kept in lowercase. */
const address = z
  .string()
  .regex(/^0x[0-9a-fA-F]{40}$/, "must be an EVM address")
  .transform((value) => value.toLowerCase() as `0x${string}`);

/** An ISO 8601 time with a time zone, such as "2026-10-10T00:00:00Z". */
const instant = z.iso.datetime({ offset: true }).transform((value) => new Date(value));

/** The most submissions a campaign may take from each of a creator's accounts (`L`, §6.4). */
export const MAX_SUBMISSION_LIMIT = 3;

/** The most accounts a creator may take part with in one campaign (§6.4). */
export const CAMPAIGN_ACCOUNT_LIMIT = 5;

/**
 * A campaign request as submitted: the brand's details and the proposed terms. A change of a
 * request carries the same, in full (D25).
 */
export const campaignRequestSchema = z.object({
  /** The brand's name, as creators will see it. */
  brandName: z.string().trim().min(2).max(80),

  /** Where the admin team can reach the brand about this request. Never published. */
  contactEmail: z.email().max(254),

  platform: z.enum(PLATFORMS),

  /** DEDICATED or INTEGRATION for YouTube. Null for X. */
  format: z.enum(YOUTUBE_FORMATS).nullable(),

  /** The campaign token's address. One of the tokens the platform accepts. */
  token: address,

  /** Budget `B` in the token's base units. */
  budget: positiveInteger,

  /** Target `T` in the platform's primary metric. */
  target: positiveInteger,

  /** Start and end of the campaign window `[startAt, endAt)`. */
  startAt: instant,
  endAt: instant,

  /** Most accepted submissions per creator account (`L`, §6.4): 1 to 3. */
  submissionLimit: z.number().int().min(1).max(MAX_SUBMISSION_LIMIT),

  brief: briefSchema,
});

/** Request JSON as the API receives it. */
export type CampaignRequestInput = z.input<typeof campaignRequestSchema>;

/** Request terms after parsing: amounts as bigint, times as Date. */
export type CampaignRequestTerms = z.output<typeof campaignRequestSchema>;
